import React, { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  CalendarCheck,
  CreditCard,
  CheckCircle2,
  Building2,
  X,
  Check,
  Clock,
  MessageCircle,
  FileSpreadsheet,
  AlertTriangle,
  ShoppingBag,
  Mail,
  Smartphone,
  Baby,
  ScanFace,
} from 'lucide-react';
import StoreBadges from 'app/javascript/components/shared/StoreBadges';
import { ROUTES } from 'app/javascript/constants/routes';
import Footer from 'app/javascript/components/shared/Footer';
import SEO from 'app/javascript/components/shared/SEO';
import LaunchReelPlayer from 'app/javascript/components/marketing/LaunchReelPlayer';
import { track } from 'app/javascript/utils/analytics';

interface AdvantageCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  highlights: string[];
  visual: React.ReactNode;
  index: number;
  link?: { to: string; label: string };
  visualVariant?: 'default' | 'image';
  badge?: string;
}

const AdvantageCard: React.FC<AdvantageCardProps> = ({
  icon: Icon,
  title,
  description,
  highlights,
  visual,
  index,
  link,
  visualVariant = 'default',
  badge,
}) => {
  const cardRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          card.classList.add('animate-card-in');
          observer.unobserve(card);
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  const rootClass =
    'opacity-0 group relative block rounded-2xl bg-slate-900/60 border border-slate-700/40 overflow-hidden hover:border-yellow-400/30 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-400/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60 focus-visible:border-yellow-400/40';

  const cardBody = (
    <>
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/0 to-amber-500/0 group-hover:from-yellow-400/5 group-hover:to-amber-500/5 transition-all duration-500" />

      <div className="relative z-10 p-6 md:p-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-5 shadow-lg shadow-yellow-400/20 group-hover:scale-110 transition-all duration-500">
              <Icon className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
            </div>

            {badge && (
              <span className="inline-flex items-center px-3 py-1 mb-3 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                {badge}
              </span>
            )}
            <h3 className="font-bebas text-2xl md:text-3xl text-white mb-3 tracking-wide">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">{description}</p>

            <ul className="space-y-2.5">
              {highlights.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-300">{item}</span>
                </li>
              ))}
            </ul>

            {link && (
              <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-yellow-400 group-hover:text-yellow-300 transition-colors">
                {link.label}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </span>
            )}
          </div>

          <div
            className={`${
              visualVariant === 'image' ? 'lg:w-96' : 'lg:w-52'
            } flex-shrink-0 flex items-center justify-center`}
          >
            <div
              className={
                visualVariant === 'image'
                  ? 'w-full rounded-xl overflow-hidden border border-slate-700/40 shadow-xl shadow-black/30'
                  : 'w-full p-5 rounded-xl bg-slate-800/60 border border-slate-700/30 backdrop-blur-sm'
              }
            >
              {visual}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (link) {
    return (
      <Link
        ref={(el) => { cardRef.current = el; }}
        to={link.to}
        onClick={() => track('cross_link_click', { page: 'para-equipes', target: link.to })}
        className={rootClass}
        style={{ animationDelay: `${index * 120}ms` }}
        aria-label={`${title} — ${link.label}`}
      >
        {cardBody}
      </Link>
    );
  }

  return (
    <div ref={(el) => { cardRef.current = el; }} className={rootClass} style={{ animationDelay: `${index * 120}ms` }}>
      {cardBody}
    </div>
  );
};

// `soon` marks a feature the plan will include once it launches.
function PricingFeature({
  children,
  included = false,
  soon = false,
}: {
  children: React.ReactNode;
  included?: boolean;
  soon?: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      {soon ? (
        <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" aria-hidden="true" />
      ) : included ? (
        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      ) : (
        <X className="w-4 h-4 text-slate-700 flex-shrink-0" />
      )}
      <span className={included || soon ? 'text-slate-200' : 'text-slate-600'}>
        {children}
        {soon && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 align-middle whitespace-nowrap rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
            Em breve
          </span>
        )}
      </span>
    </div>
  );
}

export default function CompaniesLandingPage() {
  // Academies are created in the app, so every CTA sends the visitor to the
  // store (Rails picks App Store or Play Store by user agent).
  return (
    <main className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <SEO
        title="BlackBelts | Sistema de gestão para academias de Jiu-Jitsu"
        description="Presença por reconhecimento facial, graduações, turma kids e, em breve, cobrança de mensalidade. O sistema de gestão da sua academia de Jiu-Jitsu, no tatame."
      />

      {/* Tatami Pattern Background */}
      <div
        className="fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #fbbf24 1px, transparent 1px),
            linear-gradient(180deg, #fbbf24 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950 pointer-events-none" />

      {/* ── Hero Section ── */}
      <section className="relative z-10 px-4 sm:px-6 pt-16 md:pt-28 pb-16 md:pb-24">
        <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-yellow-400/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight mb-6 animate-fade-in">
                <span className="text-white">O SISTEMA DE GESTÃO</span>
                <br />
                <span className="text-white">DA SUA ACADEMIA</span>
                <br />
                <span className="text-yellow-400">DE JIU-JITSU.</span>
              </h1>

              <p className="text-base md:text-lg text-slate-400 max-w-lg mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '100ms' }}>
                Presença comprovada por reconhecimento facial e, em breve, mensalidade sem esforço. Volte pro tatame — nós
                ajudamos com o resto.
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
                <a
                  href={ROUTES.DOWNLOAD}
                  onClick={() => track('cta_click', { page: 'para-equipes', position: 'hero' })}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-lg uppercase tracking-wide shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/30 transition-all duration-300"
                >
                  Comece grátis
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
                <span className="text-sm text-slate-500">Sem cartão de crédito</span>
              </div>
            </div>

            <div className="flex-1 flex justify-center animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="relative w-full max-w-xs sm:max-w-sm">
                <div className="absolute -inset-6 md:-inset-8 bg-yellow-400/12 rounded-[3rem] blur-3xl -z-10" />
                <LaunchReelPlayer
                  src="/launch-reel-v1"
                  poster="/launch-reel-v1-poster.jpg"
                  ariaLabel="Vídeo de demonstração: como o BlackBelts marca presença automaticamente"
                  className="relative rounded-2xl md:rounded-3xl overflow-hidden border border-slate-700/60 shadow-2xl shadow-black/50 bg-slate-900"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-bebas text-3xl sm:text-4xl md:text-5xl text-white text-center mb-4">
            VOCÊ AINDA GERENCIA SUA ACADEMIA ASSIM?
          </h2>
          <p className="text-lg text-slate-400 text-center max-w-2xl mx-auto mb-12">
            Se alguma dessas situações soa familiar, é hora de mudar.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: FileSpreadsheet, text: 'Controlando presença em caderninho ou planilha que ninguém atualiza' },
              { icon: MessageCircle, text: 'Cobrando mensalidade por WhatsApp todo mês e correndo atrás de quem não paga' },
              { icon: AlertTriangle, text: 'Sem saber quais alunos estão inadimplentes até ser tarde demais' },
              { icon: Clock, text: 'Perdendo tempo com burocracia em vez de estar no tatame ensinando' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-start gap-4 p-5 rounded-xl bg-red-500/5 border border-red-500/10">
                <Icon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 text-sm leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Advantages Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">DUAS DORES. UM SISTEMA.</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Presença comprovada por foto e, em breve, mensalidade sem esforço — feito pra academia de Jiu-Jitsu.
            </p>
          </div>

          <div className="space-y-6">
            <AdvantageCard
              index={0}
              icon={CalendarCheck}
              title="PRESENÇA POR RECONHECIMENTO FACIAL"
              description="Uma foto da turma no final do treino e o BlackBelts marca a presença de todos automaticamente. Sem chamada, sem papel, sem aluno fantasma."
              highlights={[
                'Uma foto, turma inteira presente',
                'Presença sempre comprovada por imagem',
                'Histórico por turma, horário e dia',
                'Aluno fantasma exposto antes de cancelar',
              ]}
              visual={
                <picture>
                  <source srcSet="/face-recognition-team.webp" type="image/webp" />
                  <img
                    src="/face-recognition-team.png"
                    alt="Foto de uma turma com 10 alunos identificados pelo reconhecimento facial do BlackBelts"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </picture>
              }
              visualVariant="image"
              link={{ to: ROUTES.FACE_RECOGNITION_LANDING, label: 'Conheça o reconhecimento facial' }}
            />

            <AdvantageCard
              index={1}
              icon={CreditCard}
              title="COBRANÇA DE MENSALIDADE"
              badge="Em breve"
              description="Mensalidade sem esforço — em Pix, boleto ou cartão, com a inadimplência à vista. Chega em breve aos planos Starter e Pro."
              highlights={[
                'Cobrança recorrente automática',
                'Pix, boleto e cartão no checkout',
                'Inadimplência exposta em tempo real',
              ]}
              visual={
                <picture>
                  <source srcSet="/financeiro-dashboard.webp" type="image/webp" />
                  <img
                    src="/financeiro-dashboard.png"
                    alt="Prévia do painel financeiro do BlackBelts, com saldo, valores recebidos e plano ativo"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                </picture>
              }
              visualVariant="image"
            />
          </div>
        </div>
      </section>

      {/* ── Student App Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <div className="relative p-8 sm:p-10 md:p-12 rounded-3xl bg-slate-800/50 border border-slate-700/50 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400/8 rounded-full blur-[100px]" aria-hidden="true" />

            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12">
              <div className="flex-1">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                  <Smartphone className="w-3.5 h-3.5" aria-hidden="true" />
                  App para alunos
                </span>
                <h2 className="font-bebas text-3xl sm:text-4xl md:text-5xl text-white mb-4">
                  SEUS ALUNOS ACOMPANHAM TUDO PELO APP
                </h2>
                <p className="text-slate-400 leading-relaxed max-w-xl mb-6">
                  Presenças confirmadas, graduações e o perfil da equipe: cada aluno leva a própria
                  jornada no bolso, com notificações a cada check-in. Indique o download na matrícula.
                </p>
                <ul className="space-y-2.5 max-w-xl">
                  {[
                    'Cadastro por QR Code: imprima o cartaz, o aluno aponta a câmera e entra sozinho',
                    'Categoria de peso e divisão etária CBJJ direto no perfil do atleta',
                    'Streak de treinos e aviso de semana sem treinar — o app sente falta do aluno antes de você',
                    'Foto do treino com a marca da academia, pronta pra compartilhar',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col items-start gap-3">
                <StoreBadges />
                <p className="text-xs text-slate-500">Grátis na App Store e no Google Play</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Kids / Family Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
              <Baby className="w-3.5 h-3.5" aria-hidden="true" />
              Turma kids
            </span>
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">
              JIU-JITSU KIDS SEM DOR DE CABEÇA
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Criança treina, responsável acompanha — e você não vira despachante de cadastro.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              {
                icon: Baby,
                title: 'CONTA DO RESPONSÁVEL',
                description:
                  'O pai ou a mãe cria a própria conta, adiciona os filhos como dependentes — sem e-mail pra criança — e alterna entre os perfis num toque.',
              },
              {
                icon: ScanFace,
                title: 'FACIAL DO DEPENDENTE',
                description:
                  'O responsável cadastra o rosto do filho pelo próprio celular, com consentimento registrado. Na foto da turma, a presença da criança marca como a de qualquer atleta.',
              },
              {
                icon: CreditCard,
                title: 'PLANO FAMILIAR',
                badge: 'Em breve',
                description:
                  'Uma cobrança só pra família inteira: titular e dependentes no mesmo plano, no mesmo vencimento. Menos boleto circulando, menos inadimplência.',
              },
              {
                icon: Smartphone,
                title: 'O RESPONSÁVEL VÊ TUDO PELO APP',
                description:
                  'Presenças, graduações e fotos dos treinos de cada filho, no perfil de cada um — com notificação a cada check-in confirmado.',
              },
            ].map(({ icon: Icon, title, description, badge }) => (
              <div
                key={title}
                className="rounded-2xl bg-slate-900/60 border border-slate-700/40 p-6 md:p-7 hover:border-yellow-400/30 transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-yellow-400" strokeWidth={2.5} aria-hidden="true" />
                </div>
                {badge && (
                  <span className="inline-flex items-center px-3 py-1 mb-3 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
                    {badge}
                  </span>
                )}
                <h3 className="font-bebas text-xl md:text-2xl text-white mb-3 tracking-wide">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Official Store Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-semibold uppercase tracking-wider">
              <ShoppingBag className="w-3.5 h-3.5" aria-hidden="true" />
              Novidade
            </span>
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">A LOJA OFICIAL DA SUA EQUIPE</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Kimonos, rashguards, shorts e camisetas do seu time, num catálogo com a cara da equipe. O aluno escolhe e
              fala direto com você no WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { src: '/store/generic/01-kimono.webp', alt: 'Kimono preto de Jiu-Jitsu da loja da equipe' },
                { src: '/store/generic/02-rashguard.webp', alt: 'Rashguard preto de manga longa da loja da equipe' },
                { src: '/store/generic/04-camiseta.webp', alt: 'Camiseta preta da loja da equipe' },
                { src: '/store/generic/03-shorts.webp', alt: 'Shorts de treino preto da loja da equipe' },
              ].map(({ src, alt }) => (
                <div
                  key={src}
                  className="rounded-xl overflow-hidden border border-slate-700/40 bg-slate-900/60 shadow-xl shadow-black/30"
                >
                  <img src={src} alt={alt} className="w-full h-auto block aspect-square object-cover" loading="lazy" />
                </div>
              ))}
            </div>

            <div>
              <ul className="space-y-3 mb-8">
                {[
                  'Catálogo próprio com a identidade do seu time',
                  'Kimonos, rashguards, shorts e camisetas',
                  'Aluno compra falando direto com você no WhatsApp',
                  'Link público para divulgar nas redes da equipe',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Consulte notice */}
              <div className="rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 sm:p-6">
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  A loja oficial é montada sob medida para cada equipe.{' '}
                  <span className="text-white font-semibold">Consulte</span> para criar a loja do seu time.
                </p>
                <div className="flex">
                  <a
                    href="mailto:suporte@blackbelts.app?subject=Loja oficial da minha equipe"
                    onClick={() => track('cta_click', { page: 'para-equipes', position: 'store-consult' })}
                    className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-base uppercase tracking-wide shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/30 transition-all"
                  >
                    <Mail className="w-4 h-4" aria-hidden="true" />
                    Consulte
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Section ── */}
      <section className="relative z-10 px-4 sm:px-6 py-16 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">
              PLANOS PARA CADA ACADEMIA DE JIU-JITSU
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">Comece grátis e faça upgrade quando seu tatame encher</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
            {/* Grátis */}
            <div className="flex flex-col rounded-2xl border border-slate-700/50 bg-slate-900/60 p-8">
              <h3 className="font-bebas text-3xl tracking-wide text-white">Grátis</h3>
              <div className="mt-2 h-14 flex items-baseline">
                <span className="text-4xl font-bold text-slate-400">R$ 0</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">Para academias que estão começando</p>
              <div className="space-y-3 mb-8 flex-1">
                <PricingFeature included>Até 120 membros</PricingFeature>
                <PricingFeature>Cobrança de mensalidades</PricingFeature>
                <PricingFeature included>20 fotos de controle de presença por reconhecimento facial</PricingFeature>
                <PricingFeature included>Graduações e faixas</PricingFeature>
                <PricingFeature included>Perfil público da equipe</PricingFeature>
                <PricingFeature included>Grade de horários e aulas avulsas</PricingFeature>
                <PricingFeature included>App para alunos com cadastro por QR Code</PricingFeature>
                <PricingFeature>Painel financeiro com inadimplência em tempo real</PricingFeature>
              </div>
              <a
                href={ROUTES.DOWNLOAD}
                onClick={() => track('cta_click', { page: 'para-equipes', position: 'pricing-gratis' })}
                className="block text-center py-3 px-4 rounded-xl border border-slate-600 font-bebas text-lg uppercase tracking-wide text-slate-400 hover:border-slate-500 hover:text-white transition-all"
              >
                Começar Grátis
              </a>
            </div>

            {/* Starter */}
            <div className="flex flex-col rounded-2xl border-2 border-yellow-400/50 bg-slate-800/60 p-8 relative">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wider">
                Mais Popular
              </div>
              <h3 className="font-bebas text-3xl tracking-wide text-white">Starter</h3>
              <div className="mt-2 h-14 flex items-baseline gap-2">
                <span className="text-lg text-slate-500 line-through">R$ 249</span>
                <span className="text-4xl font-bold text-white">R$ 149</span>
                <span className="text-sm text-slate-500 ml-1">/mês</span>
              </div>
              <p className="text-xs font-semibold text-yellow-400 mb-1">
                Preço promocional até o fim de 2026
              </p>
              <p className="text-sm text-slate-400 mb-6">Para academias em crescimento</p>
              <div className="space-y-3 mb-8 flex-1">
                <PricingFeature included>Até 150 membros</PricingFeature>
                <PricingFeature soon>Cobrança de mensalidades em Pix, boleto e cartão</PricingFeature>
                <PricingFeature included>Controle de presença por reconhecimento facial ilimitado</PricingFeature>
                <PricingFeature included>Graduações e faixas</PricingFeature>
                <PricingFeature included>Perfil público da equipe</PricingFeature>
                <PricingFeature included>Grade de horários e aulas avulsas</PricingFeature>
                <PricingFeature included>App para alunos com cadastro por QR Code</PricingFeature>
                <PricingFeature soon>Painel financeiro com inadimplência em tempo real</PricingFeature>
                <PricingFeature soon>Planos familiares e cobranças avulsas em até 12x</PricingFeature>
              </div>
              <a
                href={ROUTES.DOWNLOAD}
                onClick={() => track('cta_click', { page: 'para-equipes', position: 'pricing-starter' })}
                className="block text-center py-3 px-4 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-lg uppercase tracking-wide hover:bg-yellow-300 shadow-lg shadow-yellow-400/20 transition-all"
              >
                Assinar Starter
              </a>
            </div>

            {/* Pro */}
            <div className="flex flex-col rounded-2xl border border-slate-700/50 bg-slate-900/60 p-8">
              <h3 className="font-bebas text-3xl tracking-wide text-white">Pro</h3>
              <div className="mt-2 h-14 flex items-baseline gap-2">
                <span className="text-lg text-slate-500 line-through">R$ 419</span>
                <span className="text-4xl font-bold text-white">R$ 249</span>
                <span className="text-sm text-slate-500 ml-1">/mês</span>
              </div>
              <p className="text-xs font-semibold text-yellow-400 mb-1">
                Preço promocional até o fim de 2026
              </p>
              <p className="text-sm text-slate-400 mb-6">Para academias consolidadas</p>
              <div className="space-y-3 mb-8 flex-1">
                <PricingFeature included>Sem limite de membros</PricingFeature>
                <PricingFeature soon>Cobrança de mensalidades em Pix, boleto e cartão</PricingFeature>
                <PricingFeature included>Controle de presença por reconhecimento facial ilimitado</PricingFeature>
                <PricingFeature included>Graduações e faixas</PricingFeature>
                <PricingFeature included>Perfil público da equipe</PricingFeature>
                <PricingFeature included>Grade de horários e aulas avulsas</PricingFeature>
                <PricingFeature included>App para alunos com cadastro por QR Code</PricingFeature>
                <PricingFeature soon>Painel financeiro com inadimplência em tempo real</PricingFeature>
                <PricingFeature soon>Planos familiares e cobranças avulsas em até 12x</PricingFeature>
                <PricingFeature included>Suporte prioritário</PricingFeature>
              </div>
              <a
                href={ROUTES.DOWNLOAD}
                onClick={() => track('cta_click', { page: 'para-equipes', position: 'pricing-pro' })}
                className="block text-center py-3 px-4 rounded-xl border border-slate-600 font-bebas text-lg uppercase tracking-wide text-slate-400 hover:border-slate-500 hover:text-white transition-all"
              >
                Assinar Pro
              </a>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">Comece grátis — sem cartão de crédito. Upgrade ou cancele a qualquer momento.</p>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 px-4 sm:px-6 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-12 md:p-16 rounded-3xl bg-slate-800/50 border border-slate-700/50 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-yellow-400/8 rounded-full blur-[100px]" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-yellow-400/20">
                <Building2 className="w-8 h-8 text-slate-900" strokeWidth={2.5} />
              </div>

              <h2 className="font-bebas text-3xl sm:text-4xl md:text-6xl text-white mb-4">PRONTO PARA PROFISSIONALIZAR SUA ACADEMIA DE JIU-JITSU?</h2>
              <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
                Cadastre sua academia em minutos e comece a gerenciar alunos, presenças e graduações de verdade.
              </p>

              <a
                href={ROUTES.DOWNLOAD}
                onClick={() => track('cta_click', { page: 'para-equipes', position: 'final' })}
                className="group inline-flex items-center gap-3 px-10 py-5 rounded-xl bg-yellow-400 text-slate-900 font-bebas text-lg uppercase tracking-wide shadow-lg shadow-yellow-400/20 hover:bg-yellow-300 hover:shadow-yellow-400/30 transition-all duration-300"
              >
                Comece grátis
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>

              <p className="text-xs text-slate-500 mt-6">Gratuito para começar — sem cartão de crédito</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
